import React, { useState } from 'react';
import { InvoicePreview } from './invoice/invoice-preview.component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/useRedux';
import SendInvoiceConfirmationModal from './send-invoice-confirmation-modal.component';
import './invoice-preview-modal.scss';

interface InvoicePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandName?: string;
  logoUrl?: string;
  invoiceNumber?: string;
  totalAmount?: string;
  calculatedSubAmount?: any;
  isEditMode?: boolean;
  onSendInvoice?: () => void;
  isLoading?: boolean;
  invoiceDay?: string;
  dueDay?: string;
  deductionsByFreelancer?: any;
  copiedInvoiceData?: any;
}

export const InvoicePreviewModal: React.FC<InvoicePreviewModalProps> = ({
  isOpen,
  onClose,
  brandName,
  logoUrl,
  invoiceNumber = 'INV_25082025_001',
  totalAmount = 'XXXXX,XX SEK',
  calculatedSubAmount,
  isEditMode,
  onSendInvoice,
  isLoading = false,
  invoiceDay = '',
  dueDay = '',
  deductionsByFreelancer = {},
  copiedInvoiceData,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { preliminaryInvoice, preliminaryLoading } = useAppSelector((state: any) => state?.invoice);
  const [showConfirmModal, setShowConfirmModal] = useState(false); 
  
  // Extract query parameters for warning condition
  const queryParams = new URLSearchParams(window.location.search);
  const customerIdFromQuery = parseInt(queryParams.get("customerId") || "", 10) || undefined;
  const preId = queryParams.get("preId") || undefined;
  
  const handleSendInvoiceClick = () => {
    setShowConfirmModal(true);
  };
  
  const handleConfirmSend = async () => {
    if (onSendInvoice) {
      const result = await onSendInvoice();
      // Return full result (could be boolean or API response object)
      // This allows the modal to extract isAlreadySynced if available
      return result;
    }
    return false;
  };
  
  const handleSuccess = () => {
    setShowConfirmModal(false);
    // Navigate to manage invoice page with current language
    const currentLanguage = i18n.language || 'en-us';
    navigate(`/v2/manage-invoice?lang=${currentLanguage}`);
  };
  
  const handleCloseModal = () => {
    if (!isLoading) {
      setShowConfirmModal(false);
    }
  };

  // Extract PDF data from response (matching old implementation pattern)
  // Reference: usePreliminaryInvoice.js
//   if (preliminaryResponse instanceof Blob) {
//         // The response is a blob from FileContentResult
//         pdfData = preliminaryResponse;
//       } else if (preliminaryResponse?.data instanceof Blob) {
//         // If response.data is a blob
//         pdfData = preliminaryResponse.data;
//       } else if (typeof preliminaryResponse === 'string') {
//         // Backwards compatibility for direct base64 response
//         pdfData = preliminaryResponse;
//       } else if (preliminaryResponse?.data && typeof preliminaryResponse.data === 'string') {
//         // Backwards compatibility for base64 strings in response.data
//         pdfData = preliminaryResponse.data;
//       } else if (preliminaryResponse?.pdfData) {
//         // Alternative property name
//         pdfData = preliminaryResponse.pdfData;
//       }
      
//       if (pdfData) {
//         setPdfData(pdfData);
//         return pdfData;
//       } else {
//         setError('No PDF data received from server');
//         return null;
//       }
 
  // preliminaryInvoice is now a string (object URL) from Redux
  const pdfUrl = preliminaryInvoice && typeof preliminaryInvoice === 'string' 
    ? preliminaryInvoice 
    : null;

  if (!isOpen) return null;

  return (
    <div className="invoice-preview-page">
      <div className="invoice-preview-modal__container">
        {/* Left Sidebar */}
        <div className='row'>
          <div className='col-xl-4 col-lg-4 col-md-12 col-sm-12 p-0'>
        <div className="invoice-preview-modal__sidebar">
          <div className="sidebar-header">
            <h2 className="sidebar-title">{t('PREVIEW_INVOICE')}</h2>
            <div className="sidebar-amount">
              {calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat 
                ? `${calculatedSubAmount.response.totalCalculation.amountIncludingVat} ${calculatedSubAmount.response.totalCalculation.currencyName || 'SEK'}`
                : totalAmount
              }
            </div>
          </div>

          <div className="sidebar-status">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" fill="#E8F4FD" stroke="#1890FF" strokeWidth="1.5"/>
              <circle cx="8" cy="8" r="3" fill="#1890FF"/>
            </svg>
            <span>{t('IN_DRAFT')}</span>
          </div>

          <div className="sidebar-actions">
            {/* <button className="action-btn download-btn">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5M5.83333 8.33333L10 12.5M10 12.5L14.1667 8.33333M10 12.5V2.5" stroke="#544196" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('DOWNLOAD')}
            </button>

            <button className="action-btn print-btn">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5V2.5H15V7.5M5 15H3.33333C2.89131 15 2.46738 14.8244 2.15482 14.5118C1.84226 14.1993 1.66667 13.7754 1.66667 13.3333V10C1.66667 9.55797 1.84226 9.13405 2.15482 8.82149C2.46738 8.50893 2.89131 8.33333 3.33333 8.33333H16.6667C17.1087 8.33333 17.5326 8.50893 17.8452 8.82149C18.1577 9.13405 18.3333 9.55797 18.3333 10V13.3333C18.3333 13.7754 18.1577 14.1993 17.8452 14.5118C17.5326 14.8244 17.1087 15 16.6667 15H15M5 12.5H15V17.5H5V12.5Z" stroke="#544196" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('PRINT')}
            </button> */}

            <button className="action-btn send-btn" onClick={handleSendInvoiceClick} disabled={isLoading}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M18.3333 2.5L9.16667 11.6667M18.3333 2.5L12.5 18.3333L9.16667 11.6667M18.3333 2.5L1.66667 7.5L9.16667 11.6667" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {isEditMode ? t('EDIT_REQUEST_INVOICE') : t('SEND_INVOICE')}
            </button>
          </div>

          {/* <div className="sidebar-email-section"> */}
            {/* <h3 className="section-title">Email will be sent to :</h3>
            <div className="email-input-group">
              <input type="email" placeholder="Enter email id" className="email-input" />
              <button className="add-email-btn">Add</button>
            </div> */}
            {/* <div className="email-list">
              <div className="email-item">
                <span>jainankit9340@gmail.com</span>
                <button className="remove-email">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4l8 8" stroke="#E33B32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              <div className="email-item">
                <span>hiHanna@gmail.com</span>
                <button className="remove-email">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4l8 8" stroke="#E33B32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div> */}
          {/* </div> */}

          {(() => {
            // Extract all files from deductions, copiedInvoiceData.file, and workingHours
            const allFiles: Array<{ name: string; file: File | Blob; deductionType: string }> = [];
             
            // 1. Extract files from deductionsByFreelancer
            Object.keys(deductionsByFreelancer).forEach((freelancerId) => {
              const deductions = deductionsByFreelancer[freelancerId] || [];
              
              deductions.forEach((deduction: any, idx: number) => {
               
                if (deduction.files && deduction.files.length > 0) {
                  deduction.files.forEach((file: File) => {
                    allFiles.push({
                      name: file.name,
                      file: file,
                      deductionType: deduction.deductionType || 'document'
                    });
                  });
                }
              });
            });

            // 2. Extract files from copiedInvoiceData.file[0].fileContent
            if (copiedInvoiceData?.file && Array.isArray(copiedInvoiceData.file)) {
              copiedInvoiceData.file.forEach((fileItem: any, idx: number) => {
                if (fileItem.fileContent) {
                  try {
                    // Convert base64 or raw content to Blob
                    const blob = new Blob([fileItem.fileContent], { type: 'application/octet-stream' });
                    allFiles.push({
                      name: fileItem.fileName || `file_${idx + 1}`,
                      file: blob,
                      deductionType: 'invoice_file'
                    });
                  } catch (error) {
                    console.error('Error processing copiedInvoiceData file:', error);
                  }
                }
              });
            }

            // 3. Extract files from copiedInvoiceData.workingHours[index].fileContent
            if (copiedInvoiceData?.workingHours && Array.isArray(copiedInvoiceData.workingHours)) {
              copiedInvoiceData.workingHours.forEach((workingHour: any, idx: number) => {
                if (workingHour.fileContent) {
                  try {
                    // Convert base64 or raw content to Blob
                    const blob = new Blob([workingHour.fileContent], { type: 'application/octet-stream' });
                    allFiles.push({
                      name: workingHour.fileName || `working_hour_${idx + 1}`,
                      file: blob,
                      deductionType: 'working_hour_file'
                    });
                  } catch (error) {
                    console.error('Error processing workingHours file:', error);
                  }
                }
              });
            }
            
           
            if (allFiles.length === 0) return null;
            
            return (
              <div className="sidebar-attachments">
                <h3 className="section-title">{t('ADDED_ATTACHMENT')}</h3>
                <div className="attachment-list">
                  {allFiles.map((fileItem, index) => (
                    <div 
                      key={index} 
                      className="attachment-item"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        // Create object URL and open in new tab
                        const fileUrl = URL.createObjectURL(fileItem.file);
                        window.open(fileUrl, '_blank');
                      }}
                      title={t('CLICK_TO_VIEW_ATTACHMENT')}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M11.6667 1.66667H5C4.55797 1.66667 4.13405 1.84226 3.82149 2.15482C3.50893 2.46738 3.33333 2.89131 3.33333 3.33333V16.6667C3.33333 17.1087 3.50893 17.5326 3.82149 17.8452C4.13405 18.1577 4.55797 18.3333 5 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66667L11.6667 1.66667Z" stroke="#E33B32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="11.6667,1.66667 11.6667,6.66667 16.6667,6.66667" stroke="#E33B32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{fileItem.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div></div>

<div className='col-xl-8 col-lg-8 col-md-12 col-sm-12 p-0'>
        {/* Right Content Area */}
        <div className="invoice-preview-modal__content">
          <button className="invoice-preview-modal__close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#252525" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <div className="invoice-preview-modal__header">
            <h1 className="preview-title">{t('PRELIMINARY_INVOICE_PREVIEW')}</h1>
          </div>

          <div className="invoice-preview-modal__preview-wrapper">
            {preliminaryLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '700px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">{t('LOADING')}</span>
                </div>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl ?? undefined}
                width="100%"
                height="700px"
                style={{ border: 'none' }}
                title={t('PRELIMINARY_INVOICE_PDF')}
              />
            ) : (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '700px' }}>
                <p className="text-muted">{t('NO_PDF_DATA_AVAILABLE')}</p>
              </div>
            )}
          </div>
        </div>
        </div>
        </div>
      </div>
      
      <SendInvoiceConfirmationModal
        show={showConfirmModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSend}
        onSuccess={handleSuccess}
        isLoading={isLoading}
        isEditMode={isEditMode}
        invoiceAmount={totalAmount}
        invoiceDay={invoiceDay}
        dueDay={dueDay}
        calculatedSubAmount={calculatedSubAmount}
        customerIdFromQuery={customerIdFromQuery}
        preId={preId}
        dueDateFromEdit={dueDay}
      />
    </div>
  );
};

export default InvoicePreviewModal;
